// src/main.tsx
// Must stay the first import: see polyfills.ts.
import './polyfills';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import '@neuro/design-system/styles.css';
import '@neuro/design-system-patterns/styles.css';
import './components/layout/layout.css';
import './styles/spinner.css';
import './styles/responsive.css';
import './styles/plugin-ux-polish.css';
import './index.css';
import { BrowserRouter } from 'react-router-dom';
import { RealtimeProvider } from './api/realtime';
import { NotificationProvider } from './contexts/NotificationContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
// PWA is only enabled for hosted/web deployments, not desktop builds.
// In Vite, we detect this via a build-time define — see vite.config.ts `define`.
declare const __NEURO_DESKTOP__: boolean | undefined;
const isDesktop = typeof __NEURO_DESKTOP__ !== 'undefined' && __NEURO_DESKTOP__;

// Initialize monitoring and analytics
import { initSentry } from './utils/sentry';
import { initAnalytics } from './utils/analytics';
import { initPerformanceMonitoring } from './utils/performance';
import { initGlobalErrorHandlers } from './utils/errorHandler';

// Phase 4.3: Unregister any stale service workers in dev mode
// to prevent caching interference with plugin hot-reload.
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
      console.log('[Dev] Unregistered service worker:', registration.scope);
    }
  });
}

// Initialize error tracking first
initSentry();
initGlobalErrorHandlers();

// Initialize analytics and performance monitoring
initAnalytics();
initPerformanceMonitoring();

const rootElement = document.getElementById('root') as HTMLElement;

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <RealtimeProvider>
            <NotificationProvider>
              <App />
            </NotificationProvider>
          </RealtimeProvider>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
